export function usePageEditor(
  pageBeingEdited: number | null,
  setPageBeingEdited: (page: number | null) => void,
  pageEditorContent: string,
  setPageEditorContent: (content: string) => void,
  pageEditorColumns: number,
  setPageEditorColumns: (columns: number) => void,
  showPageEditor: boolean,
  setShowPageEditor: (show: boolean) => void,
  pageConfigs: any,
  setPageConfigs: any,
  selectedElement: number,
  setSelectedElement: (element: number | null) => void,
  tempContent: string,
  setTempContent: (content: string) => void,
  showEditor: boolean,
  setShowEditor: (show: boolean) => void,
  DataFrontPage: any,
  setDataFrontPage: any,
  questionPages: any,
  setQuestionPages: any,
  pageColumns: any,
  setPageColumns: any,
  numeroColunas: number,
  fontSizeQuestoes: number | "default",
  QUESTION_MARGIN: number,
  recalcPages: any,
  recalcPagesEditAsync: any,
  recalcPagesFromQuestionPage: any,
  needsRecalc: boolean,
  setNeedsRecalc: (needsRecalc: boolean) => void,
  editMode?: boolean,
  printMode?: boolean,
  isEditing?: boolean,
  setIsEditing?: (isEditing: boolean) => void
) {
  // CONSTANTES DE LAYOUT (A4)
  const A4_WIDTH = 792;
  //const A4_HEIGHT = 1119.5;
  const appliedFontSize =
    typeof fontSizeQuestoes === "number" ? fontSizeQuestoes : 13;

  /**
   * Adiciona um novo elemento à capa.
   */
  const handleAddElement = () => {
    const newElement = {
      element: "<div>Novo elemento</div>",
      x: 50,
      y: 50,
      width: 200,
      height: 100,
    };
    setDataFrontPage([...DataFrontPage, newElement]);
    setSelectedElement(DataFrontPage.length);
    setTempContent("<div>Novo elemento</div>");
    setShowEditor(true);
  };

  /**
   * Atualiza o número de colunas da página em edição.
   */
  const handleColumnChange = (newColumns: number) => {
    if (pageBeingEdited !== null) {
      setPageEditorColumns(newColumns);
      setPageConfigs((prev: any) => ({
        ...prev,
        [pageBeingEdited]: { ...prev[pageBeingEdited], columns: newColumns },
      }));
    }
  };

  /**
   * Abre o editor para um elemento específico da capa.
   */
  const handleEditElement = () => {
    if (selectedElement !== null) {
      setTempContent(DataFrontPage[selectedElement].element);
      setShowEditor(true);
    }
  };

  /**
   * Atualiza o conteúdo temporário enquanto o usuário edita.
   */
  const handleEditorChange = (content: string) => {
    setTempContent(content);
  };

  /**
   * Confirma a edição do elemento da capa e atualiza o estado.
   */
  const handleConfirmEdit = () => {
    if (selectedElement === null || selectedElement >= DataFrontPage.length)
      return;
    const updated = [...DataFrontPage];
    updated[selectedElement] = {
      ...updated[selectedElement],
      element: tempContent,
    };
    setDataFrontPage(updated);
    setShowEditor(false);
  };

  /**
   * Cancela a edição do elemento da capa.
   */
  const handleCancelEdit = () => {
    setShowEditor(false);
    setTempContent("");
  };

  /**
   * Remove o elemento selecionado da capa.
   */
  const handleRemoveElement = () => {
    if (selectedElement === null) return;
    const updated = DataFrontPage.filter(
      (_: any, idx: number) => idx !== selectedElement
    );
    setDataFrontPage(updated);
    setSelectedElement(null);
  };

  /**
   * Atualiza a posição da capa quando o usuário para de arrastar.
   */
  const handleDragStopCover = (
    index: number,
    position: { x: number; y: number }
  ) => {
    const updated = [...DataFrontPage];
    updated[index] = {
      ...updated[index],
      x: position.x,
      y: position.y,
    };
    setDataFrontPage(updated);
  };

  /**
   * Atualiza a dimensão e posição da capa ao redimensioná-la.
   */
  const handleResizeCover = (
    index: number,
    ref: HTMLElement,
    position: { x: number; y: number }
  ) => {
    const updated = [...DataFrontPage];
    updated[index] = {
      ...updated[index],
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    };
    setDataFrontPage(updated);
  };

  /**
   * Realiza a edição inline de um elemento de uma página de questões.
   */
  const handleInlineEdit = (
    pageIndex: number,
    elementIndex: number,
    newHtml: string
  ) => {
    setQuestionPages((prev: any) => {
      const updatedPages = [...prev];
      const element = updatedPages[pageIndex].elements[elementIndex];
      element.element = newHtml;

      // Mede a altura do conteúdo atualizado
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = newHtml;
      tempDiv.style.width = `${element.width}px`;
      tempDiv.style.fontSize = `${appliedFontSize}px`;
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      document.body.appendChild(tempDiv);

      const measuredHeight = tempDiv.offsetHeight + QUESTION_MARGIN;
      document.body.removeChild(tempDiv);
      element.height = measuredHeight;

      return updatedPages;
    });

    // Chama recalcPages após uma pequena pausa para evitar atualização síncrona imediata
    setTimeout(() => {
      recalcPages();
    }, 0);
  };

  /**
   * Atualiza a posição de um elemento em uma página de questões após arrastar.
   */
  const handleDragStopQuestion = (
    pageIndex: number,
    elementIndex: number,
    position: { x: number; y: number }
  ) => {
    const updated = [...questionPages];
    updated[pageIndex].elements[elementIndex] = {
      ...updated[pageIndex].elements[elementIndex],
      x: position.x,
      y: position.y,
    };
    setQuestionPages(updated);
  };

  /**
   * Atualiza as dimensões e posição de um elemento em uma página de questões ao redimensioná-lo.
   */
  const handleResizeQuestion = (
    pageIndex: number,
    elementIndex: number,
    ref: HTMLElement,
    position: { x: number; y: number }
  ) => {
    const updated = [...questionPages];
    updated[pageIndex].elements[elementIndex] = {
      ...updated[pageIndex].elements[elementIndex],
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height),
      x: position.x,
      y: position.y,
    };
    setQuestionPages(updated);
  };

  /**
   * Prepara a edição de uma página de questões.
   * Junta o conteúdo dos elementos e define os dados iniciais do editor.
   */
  const handleEditPage = (pageIndex: number) => {

    // Usa o método handleOpenEditPage para manter consistência
    handleOpenEditPage(pageIndex);
  };

  /**
   * Abre o editor para uma página, definindo o modo de edição.
   */
  const handleOpenEditPage = (pageIndex: number) => {

    if (pageIndex >= 0 && pageIndex < questionPages.length) {
      setPageBeingEdited(pageIndex);

      if (setIsEditing) {
        setIsEditing(true);
      }


      let combinedHTML = "";

      if (questionPages[pageIndex]?.elements?.length > 0) {
        questionPages[pageIndex].elements.forEach((el: any) => {
          combinedHTML += `<div style="position: absolute; left: ${el.x}px; top: ${el.y}px; width: ${el.width}px; height: ${el.height}px;">${el.element || ""}</div>`;
        });
      }

      // Obtém o número de colunas atual da página ou usa o padrão
      const currentColumns = pageColumns[pageIndex] ?? numeroColunas;

      // Atualiza os estados
      console.debug("combinedHTML:", combinedHTML);
      setPageEditorContent(combinedHTML);
      setPageEditorColumns(currentColumns);
      setShowPageEditor(true);
    } else {
      console.error(`Índice de página inválido: ${pageIndex}`);
    }
  };

  const handleConfirmEditPage = async () => {
    try {
      if (!pageEditorContent.trim()) {
        console.warn("Tentativa de salvar conteúdo vazio");
        return;
      }

      const newPageContent = String(pageEditorContent);
      const pageIndex =
        pageBeingEdited !== null ? pageBeingEdited : questionPages.length;

      // Calcula a altura do conteúdo
      const tempDiv = document.createElement("div");
      tempDiv.style.width = `${A4_WIDTH}px`;
      tempDiv.style.fontSize = `${appliedFontSize}px`;
      tempDiv.style.position = "absolute";
      tempDiv.style.visibility = "hidden";
      tempDiv.style.overflow = "visible";
      tempDiv.innerHTML = newPageContent;
      document.body.appendChild(tempDiv);
      const measuredHeight = tempDiv.offsetHeight;
      const contentHeight = Math.max(500, measuredHeight + QUESTION_MARGIN * 4);
      document.body.removeChild(tempDiv);
      // Cria o elemento atualizado
      const updatedElement = {
        element: newPageContent,
        x: 0,
        y: 0,
        width: A4_WIDTH,
        height: contentHeight,
      };

      // Atualiza configurações de colunas
      setPageConfigs((prev: any) => ({
        ...prev,
        [pageIndex]: { columns: pageEditorColumns },
      }));

      setPageColumns((prev: any) => {
        const updated = [...prev];
        updated[pageIndex] = pageEditorColumns;
        return updated;
      });

      // Atualiza o estado das páginas
      setQuestionPages((prev: any) => {
        const updated = JSON.parse(JSON.stringify(prev));
        if (pageIndex >= updated.length) {
          updated.push({
            elements: [updatedElement],
            columns: pageEditorColumns,
            lastEdited: new Date().getTime(),
          });
        } else {
          updated[pageIndex] = {
            ...updated[pageIndex],
            elements: [updatedElement],
            columns: pageEditorColumns,
            lastEdited: new Date().getTime(),
          };
        }
        return updated;
      });

      // Após atualizar o estado, define a flag para recálculo
      setNeedsRecalc(true);

      // Finaliza a edição
      if (setIsEditing) setIsEditing(false);
      setPageBeingEdited(null);
      setPageEditorContent("");
      setShowPageEditor(false);
    } catch (error) {
      console.error("Erro crítico durante a edição de página:", error);
      alert("Ocorreu um erro ao salvar a página. Por favor, tente novamente.");
    }
  };

  /**
   * Cancela a edição da página.
   */
  const handleCancelEditPage = () => {
    setShowPageEditor(false);
    setPageBeingEdited(null);
    setPageEditorContent("");
    if (setIsEditing) {
      setIsEditing(false);
    }
  };

  return {
    handleAddElement,
    handleColumnChange,
    handleEditElement,
    handleEditorChange,
    handleConfirmEdit,
    handleCancelEdit,
    handleRemoveElement,
    handleDragStopCover,
    handleResizeCover,
    handleInlineEdit,
    handleDragStopQuestion,
    handleResizeQuestion,
    handleEditPage,
    handleOpenEditPage,
    handleConfirmEditPage,
    handleCancelEditPage,
  };
}
